import { SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from "../ui/sidebar";
import type { SidebarThreadRowProps } from "./SidebarThreadRow";
import type { ThreadStatusPill } from "../Sidebar.logic";
import type { SidebarThreadSummary } from "../../types";
import { buildWorkerSubRows, computeMasterAttentionCount } from "./sidebarWorkerRows.logic";
import { shouldRenderProjectEmptyState } from "./sidebarPolish.logic";
import { sidebarStatusDotMotionClass } from "./SidebarThreadRow.helpers";
import {
  SidebarProjectThreadSection,
  type SidebarMasterWorkerData,
} from "./SidebarProjectThreadSection";

const SHOW_MORE_BUTTON_RENDER = <button type="button" />;
const SHOW_LESS_BUTTON_RENDER = <button type="button" />;

// ---------------------------------------------------------------------------
// SidebarProjectThreadList
// ---------------------------------------------------------------------------

interface SidebarProjectThreadListProps extends Omit<
  SidebarThreadRowProps,
  "thread" | "projectCwd" | "orderedProjectThreadKeys" | "isActive" | "jumpLabel"
> {
  readonly projectKey: string;
  readonly projectExpanded: boolean;
  readonly hasOverflowingThreads: boolean;
  readonly hiddenThreadStatus: ThreadStatusPill | null;
  readonly orderedProjectThreadKeys: readonly string[];
  readonly renderedThreads: readonly SidebarThreadSummary[];
  readonly showEmptyThreadState: boolean;
  readonly shouldShowThreadPanel: boolean;
  readonly isThreadListExpanded: boolean;
  readonly projectCwd: string;
  readonly activeRouteThreadKey: string | null;
  readonly threadJumpLabelByKey: ReadonlyMap<string, string>;
  readonly attachThreadListAutoAnimateRef: (node: HTMLElement | null) => void;
  readonly expandThreadListForProject: (projectKey: string) => void;
  readonly collapseThreadListForProject: (projectKey: string) => void;
  // Worker expansion (M4.1 + M4.6)
  readonly allProjectThreads: readonly SidebarThreadSummary[];
  readonly expandedWorkersByThread: ReadonlySet<string>;
  readonly onToggleWorkers: (threadKey: string) => void;
  readonly onNavigateToWorker: (workerId: string, environmentId: string) => void;
  readonly pinnedThreadKeys: ReadonlySet<string>;
  readonly onTogglePinned: (threadKey: string) => void;
  readonly readOnlyReason: string | null;
}

export function SidebarProjectThreadList(props: SidebarProjectThreadListProps) {
  const {
    projectKey,
    projectExpanded,
    hasOverflowingThreads,
    hiddenThreadStatus,
    orderedProjectThreadKeys,
    renderedThreads,
    showEmptyThreadState,
    shouldShowThreadPanel,
    isThreadListExpanded,
    projectCwd,
    activeRouteThreadKey,
    threadJumpLabelByKey,
    attachThreadListAutoAnimateRef,
    expandThreadListForProject,
    collapseThreadListForProject,
    allProjectThreads,
    expandedWorkersByThread,
    onToggleWorkers,
    onNavigateToWorker,
    pinnedThreadKeys,
    onTogglePinned,
    readOnlyReason,
    ...threadRowProps
  } = props;

  // Build a map from master thread ID -> workers for O(1) lookup per row
  const workersByMasterKey = (() => {
    const map = new Map<string, SidebarMasterWorkerData>();
    for (const thread of renderedThreads) {
      if (thread.kind !== "master") continue;
      const workers = buildWorkerSubRows(
        allProjectThreads.map((t) => ({
          id: t.id,
          title: t.title,
          kind: t.kind,
          status: t.status,
          parentThreadId: t.parentThreadId,
          workerRole: t.workerRole,
        })),
        thread.id,
      );
      if (workers.length > 0) {
        map.set(thread.id, {
          workers,
          attention: computeMasterAttentionCount(workers),
          environmentId: thread.environmentId,
        });
      }
    }
    return map;
  })();
  const shouldRenderEmptyState = shouldRenderProjectEmptyState({
    projectExpanded,
    hasThreads: renderedThreads.length > 0,
  });

  if (
    !shouldShowThreadPanel ||
    (renderedThreads.length === 0 && !hasOverflowingThreads && !shouldRenderEmptyState)
  ) {
    return null;
  }

  return (
    <SidebarMenuSub
      ref={attachThreadListAutoAnimateRef}
      className="mx-1 my-0 w-full translate-x-0 gap-0.5 overflow-hidden border-l border-border/35 px-1.5 py-0"
    >
      {shouldShowThreadPanel && showEmptyThreadState && shouldRenderEmptyState ? (
        <SidebarMenuSubItem className="w-full" data-thread-selection-safe>
          <div
            data-thread-selection-safe
            className="flex h-4 w-full translate-x-0 items-center pl-6 pr-2 text-left text-[10px] text-muted-foreground/45"
          >
            <span>No chats</span>
          </div>
        </SidebarMenuSubItem>
      ) : null}
      {shouldShowThreadPanel ? (
        <SidebarProjectThreadSection
          threads={renderedThreads}
          projectCwd={projectCwd}
          orderedProjectThreadKeys={orderedProjectThreadKeys}
          activeRouteThreadKey={activeRouteThreadKey}
          threadJumpLabelByKey={threadJumpLabelByKey}
          workersByMasterKey={workersByMasterKey}
          expandedWorkersByThread={expandedWorkersByThread}
          onToggleWorkers={onToggleWorkers}
          onNavigateToWorker={onNavigateToWorker}
          pinnedThreadKeys={pinnedThreadKeys}
          onTogglePinned={onTogglePinned}
          readOnlyReason={readOnlyReason}
          threadRowProps={threadRowProps}
        />
      ) : null}

      {projectExpanded && hasOverflowingThreads && !isThreadListExpanded ? (
        <SidebarMenuSubItem className="w-full">
          <SidebarMenuSubButton
            render={SHOW_MORE_BUTTON_RENDER}
            data-thread-selection-safe
            size="sm"
            className="h-6 w-full translate-x-0 justify-start px-2 text-left text-[10px] text-muted-foreground/60 hover:bg-accent hover:text-muted-foreground"
            onClick={() => expandThreadListForProject(projectKey)}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              {hiddenThreadStatus ? (
                <span
                  title={hiddenThreadStatus.label}
                  className="inline-flex size-3.5 shrink-0 items-center justify-center text-muted-foreground/50"
                >
                  <span
                    className={`size-[9px] rounded-full ${hiddenThreadStatus.dotClass} ${
                      hiddenThreadStatus.pulse
                        ? "motion-safe:animate-status-pulse"
                        : sidebarStatusDotMotionClass(hiddenThreadStatus)
                    }`}
                  />
                  <span className="sr-only">{hiddenThreadStatus.label}</span>
                </span>
              ) : null}
              <span>Show more</span>
            </span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {projectExpanded && hasOverflowingThreads && isThreadListExpanded ? (
        <SidebarMenuSubItem className="w-full">
          <SidebarMenuSubButton
            render={SHOW_LESS_BUTTON_RENDER}
            data-thread-selection-safe
            size="sm"
            className="h-6 w-full translate-x-0 justify-start px-2 text-left text-[10px] text-muted-foreground/60 hover:bg-accent hover:text-muted-foreground"
            onClick={() => collapseThreadListForProject(projectKey)}
          >
            <span>Show less</span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
}
